import { useState } from "react";
import { ArrowLeft, FileDown, Loader2, CheckCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { useCreditPrompt } from "@/hooks/useCreditPrompt";
import { authFetch } from "@/utils/authFetch";

const API = process.env.REACT_APP_BACKEND_URL;

const Section = ({ title, children }) => (
  <div className="border-b-2 border-[#E4E4E7] pb-4 mb-4">
    <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
    {children}
  </div>
);

const Field = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between py-1 text-sm">
      <span className="text-[#71717A] font-medium">{label}</span>
      <span className="font-semibold text-[#0A0A0A] text-right max-w-[60%]">{String(value)}</span>
    </div>
  );
};

export default function MinorWorksStep3({ formData, onPrev, user, editCertId }) {
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const { showModal, modalCreditType, promptBuyCredits, closeModal, buyCredits } = useCreditPrompt();

  const buildPayload = () => ({
    ...formData,
    cert_date: formData.cert_date ? format(formData.cert_date, "dd/MM/yyyy") : "",
  });

  const saveContractorDetails = async () => {
    try {
      await authFetch(`${API}/api/user/contractor-details`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          contractor_trading_title: formData.contractor_trading_title,
          contractor_email: formData.contractor_email,
          contractor_phone: formData.contractor_phone,
          contractor_web: formData.contractor_web || "",
          contractor_address: formData.contractor_address,
          contractor_postcode: formData.contractor_postcode,
          contractor_reg_no: formData.contractor_reg_no,
          contractor_branch_no: formData.contractor_branch_no,
        }),
      });
    } catch { /* ignore */ }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await authFetch(`${API}/api/certificates/minor-works/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    setSubmitting(true);
    try {
      const isEdit = !!editCertId;
      const url = isEdit ? `${API}/api/certificates/${editCertId}` : `${API}/api/certificates/minor-works`;
      const method = isEdit ? "PUT" : "POST";
      const res = await authFetch(url, {
        method, headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (res.status === 402) { promptBuyCredits("electrical"); return; }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to generate certificate");
      }
      const data = await res.json();
      const certId = data.certificate_id || editCertId;
      setPdfUrl(`${API}/api/certificates/domestic-eic/${certId}/download`);
      setEmailSent(data.email_sent || false);
      setCompleted(true);
      toast.success(isEdit ? "Certificate updated!" : "Minor Works certificate generated!");
      await saveContractorDetails();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (completed) {
    return (
      <div className="form-container max-w-3xl mx-auto text-center" data-testid="mw-step-3-complete">
        <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Certificate Generated</h2>
        <p className="text-sm text-[#71717A] mb-6">
          Your Minor Works certificate has been created.
          {emailSent && " An email copy has been sent."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {pdfUrl && (
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="mw-download-pdf"
              className="brutalist-btn flex items-center justify-center gap-2">
              <FileDown className="w-4 h-4" /> Download PDF
            </a>
          )}
          <a href="/dashboard" data-testid="mw-back-dashboard" className="brutalist-btn-outline flex items-center justify-center gap-2">
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container max-w-3xl mx-auto" data-testid="mw-step-3">
      <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Review & Declaration</h2>
      <p className="text-xs text-[#71717A] mb-6">Review all details, then generate your Minor Works certificate.</p>

      <Section title="Part 1: Details of Minor Works">
        <Field label="Client" value={formData.client_name} />
        <Field label="Client email" value={formData.client_email} />
        <Field label="Description" value={formData.description_of_works} />
        <Field label="Address" value={formData.installation_address} />
        <Field label="Postcode" value={formData.installation_postcode} />
        <Field label="Departures" value={formData.departures} />
        <Field label="Comments / defects" value={formData.comments_existing} />
        <Field label="Risk assessment" value={formData.risk_assessment} />
        <Field label="Date" value={formData.cert_date ? format(formData.cert_date, "dd/MM/yyyy") : ""} />
      </Section>

      <Section title="Part 2: Earthing & Bonding">
        <Field label="System earthing" value={formData.system_earthing} />
        <Field label="Zdb (Ω)" value={formData.zdb} />
        <Field label="Adequate main conductors" value={formData.adequate_main_conductors} />
        <Field label="Bonding - Water" value={formData.bonding_water} />
        <Field label="Bonding - Gas" value={formData.bonding_gas} />
        <Field label="Bonding - Oil" value={formData.bonding_oil} />
        <Field label="Bonding - Structural" value={formData.bonding_structural} />
        <Field label="Bonding - Other" value={formData.bonding_other} />
        <Field label="Earthing conductor" value={formData.earthing_conductor} />
      </Section>

      <Section title="Part 3: Circuit Details">
        <Field label="DB ref" value={formData.db_ref} />
        <Field label="DB location & type" value={formData.db_location_type} />
        <Field label="Circuit number" value={formData.circuit_number} />
        <Field label="Circuit description" value={formData.circuit_description} />
        <Field label="Reference method" value={formData.reference_method} />
        <Field label="Live conductor (mm²)" value={formData.conductor_live_size} />
        <Field label="CPC (mm²)" value={formData.conductor_cpc_size} />
        <Field label="OCPD type" value={formData.ocpd_type} />
        <Field label="OCPD rating (A)" value={formData.ocpd_rating} />
        <Field label="RCD type" value={formData.rcd_type} />
        <Field label="RCD IΔn (mA)" value={formData.rcd_idn} />
      </Section>

      <Section title="Part 4: Test Results">
        <Field label="R1+R2 (Ω)" value={formData.r1r2} />
        <Field label="Max Zs (Ω)" value={formData.max_zs} />
        <Field label="Ring L/L (Ω)" value={formData.ring_ll} />
        <Field label="Ring N/N (Ω)" value={formData.ring_nn} />
        <Field label="IR test voltage (V)" value={formData.ir_test_voltage} />
        <Field label="IR Live-Live (MΩ)" value={formData.ir_live_live} />
        <Field label="IR Live-Earth (MΩ)" value={formData.ir_live_earth} />
        <Field label="RCD time (ms)" value={formData.rcd_disconnect_time} />
        <Field label="RCD test button" value={formData.rcd_test_btn} />
        <Field label="Polarity" value={formData.polarity} />
        <Field label="AFDD test button" value={formData.afdd_test_btn} />
        <Field label="SPD confirmed" value={formData.spd_confirmed} />
      </Section>

      <Section title="Part 5: Declaration">
        <Field label="Electrician" value={formData.electrician_name} />
        <Field label="Trading title" value={formData.contractor_trading_title} />
        <Field label="Email" value={formData.contractor_email} />
        <Field label="Phone" value={formData.contractor_phone} />
        <Field label="Address" value={formData.contractor_address} />
        <Field label="Postcode" value={formData.contractor_postcode} />
        <Field label="Registration No." value={formData.contractor_reg_no} />
      </Section>

      <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4">
        <button data-testid="mw-step-3-prev" onClick={onPrev} className="brutalist-btn-outline flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>
        <div className="flex gap-3">
          <button data-testid="mw-preview-btn" onClick={handlePreview} disabled={previewing}
            className="brutalist-btn-outline flex items-center gap-2">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Preview PDF
          </button>
          <button data-testid="mw-generate-btn" onClick={handleGenerate} disabled={submitting}
            className="brutalist-btn flex items-center gap-2">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><FileDown className="w-4 h-4" /> Generate Certificate</>}
          </button>
        </div>
      </div>
      <BuyCreditsModal open={showModal} onClose={closeModal} creditType={modalCreditType} onBuy={buyCredits} />
    </div>
  );
}
